import { useState, useCallback, type ChangeEvent } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Loader2,
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Phone,
  Download,
  X,
  ExternalLink,
  User,
  FileText,
} from "lucide-react";
import { Button, Card, CardContent, ConfirmDialog, Input, Label, cn } from "@tellescope/ui";
import { Badge } from "@tellescope/design-system";
import { formatTime, ensureHttps } from "@tellescope/utils";
import type { CalendarEvent } from "@tellescope/types-models";
import { useTellescopeQuery, useUsersMap } from "@tellescope/tanstack-query";
import { useEnduserStore } from "../../store";
import { useCancelCalendarEvent } from "../../hooks/useCancelCalendarEvent";
import { useJoinVideoCall } from "../../hooks/useJoinVideoCall";
import { detectVideoIntegration } from "../../../shared/utils/detectVideoIntegration";
import { useEventStatus } from "../../hooks/useEventStatus";
import { VideoCallButton } from "../../components/VideoCallButton";
import { getApiURL } from "../../../config/api";

/**
 * Builds a Google Calendar "Add Event" URL for a calendar event.
 */
const buildGoogleCalendarUrl = (event: CalendarEvent): string => {
  const startDate = new Date(event.startTimeInMS);
  const endDate = new Date(event.startTimeInMS + event.durationInMinutes * 60 * 1000);

  // Format dates in the required format: YYYYMMDDTHHMMSSZ
  const formatForGCal = (date: Date) =>
    date
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}/, "");

  const description = encodeURIComponent(event.displayDescription || event.description || "");

  // Determine location - could be physical or video URL
  let location = event.location || "";
  if (event.videoURL) {
    location = event.videoURL;
  } else if (event.externalVideoURL) {
    location = event.externalVideoURL;
  } else if (event.healthieZoomJoinURL) {
    location = event.healthieZoomJoinURL;
  }

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.displayTitle || event.title,
    dates: `${formatForGCal(startDate)}/${formatForGCal(endDate)}`,
    details: description,
    location: location,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

/**
 * Legacy event view page that handles direct event URLs.
 * Maps to old portal's /events/:id route.
 *
 * URL params:
 * - id: the calendar event ID
 */
export const EventViewPage = () => {
  const { t } = useTranslation("events");
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const session = useEnduserStore((state) => state.session);
  const currentEnduser = useEnduserStore((state) => state.currentEnduser);

  // Embedded mode hides navigation elements (for iframe embedding)
  const isEmbedded = searchParams.get("embedded") === "true";

  // Fetch event using TanStack Query
  const {
    data: event,
    isLoading,
    error: fetchError,
    refetch,
  } = useTellescopeQuery({
    model: "calendar_events",
    method: "getOne",
    request: id ?? "",
    enabled: !!id,
  });

  // Resolve the host user's personal meeting URL when the event is configured to use it.
  // Declared before the early returns below to keep hook order stable.
  const hostId = event?.useUserURL ? event.attendees.find((a) => a.type === "user")?.id : undefined;
  const hostUsersMap = useUsersMap(hostId ? [hostId] : []);

  // Cancel state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const { cancelEventAsync, isPending: isCancelling } = useCancelCalendarEvent();

  // Video calling
  const { joinVideoCall } = useJoinVideoCall();

  // Handle cancel confirmation
  const handleConfirmCancel = async () => {
    if (!event) return;
    try {
      await cancelEventAsync({
        eventId: event.id,
        cancelReason: cancelReason || undefined,
      });
      // Refresh event to show cancelled state
      await refetch();
      setShowCancelDialog(false);
      setCancelReason("");
    } catch (err) {
      console.error("Failed to cancel event:", err);
    }
  };

  // Handle join video call (Twilio)
  const handleJoinTwilioCall = useCallback(async () => {
    if (!event) return;
    await joinVideoCall(event);
  }, [event, joinVideoCall]);

  // Handle ICS download
  const handleDownloadIcs = () => {
    if (!event || !session) return;
    const apiUrl = getApiURL();
    const icsUrl = `${apiUrl}/v1/calendar-events/download-ics-file?authToken=${session.authToken}&calendarEventId=${event.id}`;
    window.open(icsUrl, "_blank");
  };

  // Handle Google Calendar
  const handleAddToGoogleCalendar = () => {
    if (!event) return;
    window.open(buildGoogleCalendarUrl(event), "_blank");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-muted-foreground">
          <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
          <p className="text-sm">{t("Loading event...")}</p>
        </div>
      </div>
    );
  }

  if (fetchError || !event) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-sm font-medium text-destructive mb-1">{t("Unable to load event")}</p>
          <p className="text-xs text-muted-foreground mb-4">{fetchError?.message ?? t("Event not found")}</p>
          {!isEmbedded && (
            <Link to="/events">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t("Go Back")}
              </Button>
            </Link>
          )}
        </div>
      </div>
    );
  }

  const { isCancelled, isRescheduled, isUpcoming } = useEventStatus(event, currentEnduser);
  const hostUserUrl = hostId ? hostUsersMap[hostId]?.url : undefined;
  const videoIntegration = detectVideoIntegration(event, hostUserUrl);

  const startDate = new Date(event.startTimeInMS);
  const endTime = new Date(event.startTimeInMS + event.durationInMinutes * 60 * 1000);

  // Get providers from attendees
  const providerIds = Array.from(new Set(event.attendees.filter((a) => a.type === "user").map((a) => a.id)));

  return (
    <div className={cn("container mx-auto px-4 max-w-2xl", isEmbedded ? "py-4" : "py-6")}>
      {/* Header with back button (hidden in embedded mode) */}
      {!isEmbedded && (
        <div className="flex items-center gap-3 mb-6">
          <Link to="/events">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold truncate">{event.displayTitle || event.title}</h1>
              {isCancelled && (
                <Badge color="destructive" className="flex-shrink-0">
                  {t("Cancelled")}
                </Badge>
              )}
              {isRescheduled && !isCancelled && (
                <Badge color="secondary" className="flex-shrink-0">
                  {t("Rescheduled")}
                </Badge>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Compact header for embedded mode */}
      {isEmbedded && (
        <div className="flex items-center gap-2 mb-4">
          <h1 className="text-xl font-bold truncate">{event.displayTitle || event.title}</h1>
          {isCancelled && <Badge color="destructive">{t("Cancelled")}</Badge>}
          {isRescheduled && !isCancelled && <Badge color="secondary">{t("Rescheduled")}</Badge>}
        </div>
      )}

      {/* Main Content */}
      <div className="space-y-4">
        {/* Date & Time Card */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              {/* Calendar badge */}
              <div
                className={cn(
                  "w-16 h-16 rounded-lg flex flex-col items-center justify-center flex-shrink-0",
                  isCancelled ? "bg-muted" : "bg-primary/10"
                )}
              >
                <Calendar className={cn("h-4 w-4", isCancelled ? "text-muted-foreground" : "text-primary")} />
                <span className={cn("text-xs font-medium", isCancelled ? "text-muted-foreground" : "text-primary")}>
                  {startDate.toLocaleDateString(undefined, { month: "short" })}
                </span>
                <span className={cn("text-lg font-bold", isCancelled ? "text-muted-foreground" : "text-primary")}>
                  {startDate.getDate()}
                </span>
              </div>

              {/* Date details */}
              <div className="flex-1 min-w-0">
                <p className={cn("font-medium", isCancelled && "text-muted-foreground")}>
                  {startDate.toLocaleDateString(undefined, {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
                <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                  <Clock className="h-3.5 w-3.5" />
                  <span>
                    {formatTime(event.startTimeInMS)} - {formatTime(endTime.getTime())} ({event.durationInMinutes}{" "}
                    {t("min")})
                  </span>
                </div>

                {/* Calendar actions */}
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleDownloadIcs}>
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    {t("Download ICS")}
                  </Button>
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleAddToGoogleCalendar}>
                    <Calendar className="h-3.5 w-3.5 mr-1.5" />
                    {t("Add to Google Calendar")}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Video Call Button (prominent if available) */}
        {videoIntegration.hasAnyVideo && !isCancelled && videoIntegration.videoType && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-4">
              <VideoCallButton
                event={event}
                videoType={videoIntegration.videoType}
                videoUrl={videoIntegration.primaryVideoUrl}
                size="default"
                onTwilioClick={handleJoinTwilioCall}
              />
            </CardContent>
          </Card>
        )}

        {/* Event Details Card */}
        <Card>
          <CardContent className="p-4 space-y-3">
            {/* Providers */}
            {providerIds.length > 0 && !event.portalSettings?.hideUsers && (
              <div className="flex items-start gap-3">
                <User className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {providerIds.length === 1 ? t("Provider") : t("Providers")}
                  </p>
                  <p className="text-sm">
                    {providerIds.length} {t("assigned")}
                  </p>
                </div>
              </div>
            )}

            {/* Location */}
            {event.location && (
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t("Location")}</p>
                  <p className="text-sm">{event.location}</p>
                </div>
              </div>
            )}

            {/* Location URL */}
            {event.locationURL && (
              <div className="flex items-start gap-3">
                <ExternalLink className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t("URL")}</p>
                  <a
                    href={ensureHttps(event.locationURL)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline break-all"
                  >
                    {event.locationURL}
                  </a>
                </div>
              </div>
            )}

            {/* Phone */}
            {event.phone && (
              <div className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t("Phone")}</p>
                  <a href={`tel:${event.phone}`} className="text-sm text-primary hover:underline">
                    {event.phone}
                  </a>
                </div>
              </div>
            )}

            {/* Location Notes */}
            {event.locationNotes && (
              <div className="flex items-start gap-3">
                <FileText className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t("Notes")}</p>
                  <p className="text-sm whitespace-pre-wrap">{event.locationNotes}</p>
                </div>
              </div>
            )}

            {/* Description */}
            {(event.displayDescription || event.description) && (
              <div className="flex items-start gap-3">
                <FileText className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t("Description")}</p>
                  <p className="text-sm whitespace-pre-wrap">{event.displayDescription || event.description}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cancel Button (only for upcoming, non-cancelled events) */}
        {isUpcoming && !isCancelled && !isRescheduled && !event.preventCancelInPortal && (
          <Card>
            <CardContent className="p-4">
              <Button
                variant="outline"
                className="w-full text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={() => setShowCancelDialog(true)}
              >
                <X className="h-4 w-4 mr-2" />
                {t("Cancel Appointment")}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        variant="destructive"
        title={t("Cancel Appointment")}
        description={t("Are you sure you want to cancel this appointment?")}
        confirmText={t("Yes, cancel appointment")}
        cancelText={t("Keep appointment")}
        onConfirm={handleConfirmCancel}
        loading={isCancelling}
      >
        {event?.requirePortalCancelReason && (
          <div className="mt-4">
            <Label htmlFor="cancelReason" className="text-sm font-medium">
              {t("Reason for cancellation")} *
            </Label>
            <Input
              id="cancelReason"
              value={cancelReason}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setCancelReason(e.target.value)}
              placeholder={t("Please provide a reason")}
              className="mt-1"
            />
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
};
